const LIST_LINE = /^( *)(?:[-*]|\d+\.)\s+/;

export function lineRange(body, start, end) {
  const text = String(body ?? "");
  const from = Math.max(0, Math.min(start, end));
  let to = Math.max(start, end);
  const lineStart = text.lastIndexOf("\n", from - 1) + 1;
  if (to > from && text[to - 1] === "\n") {
    to -= 1;
  }
  let lineEnd = text.indexOf("\n", to);
  if (lineEnd === -1) lineEnd = text.length;
  return { lineStart, lineEnd };
}

export function isListLine(line) {
  return LIST_LINE.test(String(line ?? ""));
}

export function selectionTouchesList(body, start, end) {
  const text = String(body ?? "");
  const { lineStart, lineEnd } = lineRange(text, start, end);
  return text.slice(lineStart, lineEnd).split("\n").some(isListLine);
}

function replaceRange(body, from, to, nextBlock, selectionStart, selectionEnd) {
  return {
    body: `${body.slice(0, from)}${nextBlock}${body.slice(to)}`,
    selectionStart,
    selectionEnd,
  };
}

export function applyWrap(body, start, end, before, after, emptyPlaceholder) {
  const text = String(body ?? "");
  let from = start;
  let to = end;
  while (from < to && /\s/.test(text[from])) from += 1;
  while (to > from && /\s/.test(text[to - 1])) to -= 1;
  const selected = text.slice(from, to);
  const inner = selected || emptyPlaceholder;
  const insertAt = selected ? from : start;
  const next = `${text.slice(0, insertAt)}${before}${inner}${after}${text.slice(selected ? to : end)}`;
  const innerStart = insertAt + before.length;
  return {
    body: next,
    selectionStart: innerStart,
    selectionEnd: innerStart + inner.length,
  };
}

export function applyLink(body, start, end) {
  const text = String(body ?? "");
  let from = start;
  let to = end;
  while (from < to && /\s/.test(text[from])) from += 1;
  while (to > from && /\s/.test(text[to - 1])) to -= 1;
  const selected = text.slice(from, to) || "текст";
  const insertAt = text.slice(from, to) ? from : start;
  const sliceEnd = text.slice(from, to) ? to : end;
  const url = "https://";
  const snippet = `[${selected}](${url})`;
  const next = `${text.slice(0, insertAt)}${snippet}${text.slice(sliceEnd)}`;
  const urlStart = insertAt + selected.length + 3;
  return {
    body: next,
    selectionStart: urlStart,
    selectionEnd: urlStart + url.length,
  };
}

export function applyHeading(body, start, end, level) {
  const text = String(body ?? "");
  const { lineStart, lineEnd } = lineRange(text, start, end);
  const prefix = `${"#".repeat(level)} `;
  const nextBlock = text
    .slice(lineStart, lineEnd)
    .split("\n")
    .map((line) => `${prefix}${line.replace(/^\s*#{1,3}\s+/, "")}`)
    .join("\n");
  return replaceRange(
    text,
    lineStart,
    lineEnd,
    nextBlock,
    lineStart,
    lineStart + nextBlock.length
  );
}

export function applyList(body, start, end, ordered) {
  const text = String(body ?? "");
  const { lineStart, lineEnd } = lineRange(text, start, end);
  let number = 1;
  const nextBlock = text
    .slice(lineStart, lineEnd)
    .split("\n")
    .map((line) => {
      if (!line.trim()) return line;
      const indent = (line.match(/^(\s*)/) || ["", ""])[1];
      const content = line.replace(/^\s*(?:[-*]|\d+\.)\s+/, "").replace(/^\s+/, "");
      const marker = ordered ? `${number++}. ` : "- ";
      return `${indent}${marker}${content}`;
    })
    .join("\n");
  return replaceRange(
    text,
    lineStart,
    lineEnd,
    nextBlock,
    lineStart,
    lineStart + nextBlock.length
  );
}

export function indentLines(body, start, end, direction) {
  const text = String(body ?? "");
  const { lineStart, lineEnd } = lineRange(text, start, end);
  const nextBlock = text
    .slice(lineStart, lineEnd)
    .split("\n")
    .map((line) => {
      if (direction > 0) return `    ${line}`;
      if (line.startsWith("    ")) return line.slice(4);
      if (line.startsWith("  ")) return line.slice(2);
      if (line.startsWith(" ")) return line.slice(1);
      return line;
    })
    .join("\n");
  return replaceRange(
    text,
    lineStart,
    lineEnd,
    nextBlock,
    lineStart,
    lineStart + nextBlock.length
  );
}

export function replaceSelection(body, start, end, snippet) {
  const text = String(body ?? "");
  const insert = String(snippet ?? "");
  const from = Math.max(0, Math.min(start, end, text.length));
  const to = Math.max(0, Math.min(Math.max(start, end), text.length));
  const next = `${text.slice(0, from)}${insert}${text.slice(to)}`;
  const caret = from + insert.length;
  return { body: next, selectionStart: caret, selectionEnd: caret };
}

export function insertBlock(body, start, end, snippet) {
  const text = String(body ?? "");
  const prefix = start > 0 && text[start - 1] !== "\n" ? "\n" : "";
  const suffix = end < text.length && text[end] !== "\n" ? "\n" : "\n";
  const inserted = `${prefix}${snippet}${suffix}`;
  const next = `${text.slice(0, start)}${inserted}${text.slice(end)}`;
  const caret = start + inserted.length;
  return { body: next, selectionStart: caret, selectionEnd: caret };
}

export function applyFormat(body, start, end, action) {
  switch (action) {
    case "bold":
      return applyWrap(body, start, end, "**", "**", "текст");
    case "italic":
      return applyWrap(body, start, end, "*", "*", "текст");
    case "link":
      return applyLink(body, start, end);
    case "h2":
      return applyHeading(body, start, end, 2);
    case "h3":
      return applyHeading(body, start, end, 3);
    case "ul":
      return applyList(body, start, end, false);
    case "ol":
      return applyList(body, start, end, true);
    case "indent":
      return indentLines(body, start, end, 1);
    case "outdent":
      return indentLines(body, start, end, -1);
    default:
      return { body, selectionStart: start, selectionEnd: end };
  }
}
